import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import switchRegion from 'lib/geo/switchRegion'
import config from 'constants/config'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const MemberText = styled.span`
  text-transform: none;
  font-weight: normal;
  white-space: nowrap;
  margin-left: ${rem(4)};
`

const MobileContainer = styled(Group)`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const InlineButton = styled(TextButton)`
  flex: 0 0 auto;
  align-self: flex-start; /* or center */
  width: auto;
`

const HeroSection = styled.div`
  padding: ${rem(48)} 0;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  /* Non-member hero uses darkContrast for platinum (tierStyles uses normalContrast) */
  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const InlineFlag = styled(Flag)`
  margin-left: ${rem(8)};
  vertical-align: middle;
`

function LoyaltyAccountLandingPageHeroNonMemberRegion() {
  const intl = useIntl()
  const [tier, tierName] = useCurrentLoyaltyTier()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const loyaltyRegion = useAppSelector(state => state.loyalty.account.data?.region)
  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyRegion)?.currencyCode
  }, [loyaltyRegion])
  const account = useLoyaltyAccount().data!

  const handleSwitchRegion = useCallback(() => {
    if (!loyaltyRegion) return
    switchRegion(loyaltyRegion)
  }, [loyaltyRegion])

  const memberText = intl.formatMessage({
    id: 'Qta45xcbFssRsx2r3ROF',
    defaultMessage: '\u00b7 Member {accountNumber}',
    description: 'Member label with dot separator and account number',
  }, { accountNumber: account.accountNumber })
  const overseasHeadingWithName = intl.formatMessage({ id: 'fOSFg_wAC9cXbpTag0mk', defaultMessage: '{firstName}, it looks like you’re overseas.', description: 'Overseas heading with user first name. firstName is displayed in italics.' }, { firstName })
  const overseasHeading = intl.formatMessage({ id: 'AsEwU66deuDO2IthB5Z9', defaultMessage: 'It looks like you’re overseas.', description: 'Overseas heading without user name on the promotional landing page' })
  const switchCurrencyMessage = loyaltyCurrency ? intl.formatMessage({ id: 'F4W31XAiX8owZ94b3s6e', defaultMessage: 'You’ll need to switch to {loyaltyCurrency} and redeem your member benefits.', description: 'Message prompting user to switch currency in the loyalty section' }, { loyaltyCurrency }) : ''
  const switchButtonText = loyaltyCurrency ? intl.formatMessage({ id: 'oxedn-Wigz-hdz124yDz', defaultMessage: 'Switch to {loyaltyCurrency}', description: 'Button text to switch to loyalty currency in the loyalty section' }, { loyaltyCurrency }) : ''

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <div>
          <Group direction="vertical" gap={8}>
            <Group
              direction="horizontal"
              gap={12}
            >
              <LoyaltyProgramNameLogo width={104} />
              <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">
                {tierName}
                <MemberText>{memberText}</MemberText>
                {loyaltyRegion && (
                  <InlineFlag size="XS" countryCode={loyaltyRegion} />
                )}
              </LoyaltyTierLabel>

            </Group>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" verticalAlign="end" horizontalAlign="space-between" desktopGap={40}>
                <div>
                  <Heading variant="heading1">
                    {firstName ? overseasHeadingWithName : overseasHeading}
                  </Heading>
                  {loyaltyCurrency && <BodyText variant="large">
                    {switchCurrencyMessage}
                  </BodyText>}
                </div>
                {loyaltyCurrency && <TextButton kind="primary" size="medium" type="submit" endIcon={<LineSyncIcon />} onClick={handleSwitchRegion}>{switchButtonText} </TextButton>}
              </Group>
            </CSSBreakpoint>
            <MobileContainer direction="vertical" gap={16}>
              <Heading variant="heading1" align="start">
                {firstName ? overseasHeadingWithName : overseasHeading}
              </Heading>
              {loyaltyCurrency && <BodyText variant="large" align="start">
                {switchCurrencyMessage}
              </BodyText>}
              {loyaltyCurrency && <InlineButton kind="primary" size="medium" endIcon={<LineSyncIcon />} onClick={handleSwitchRegion}>{switchButtonText} </InlineButton>}
            </MobileContainer>
          </Group>
        </div>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHeroNonMemberRegion
